import React, { useContext, useEffect, useRef } from 'react';
import './Input.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faMicrophoneAlt, faEllipsis, faMicrophoneSlash } from "@fortawesome/free-solid-svg-icons";
import Button from '../button/Button';
import { WEBSOCK_BASE_URL } from '../../constants';
import { AuthContext } from '../../auth';

type MicStateString = "OFF" | "CONNECTING" | "MICSETUP" | "ON" | "STOPPING" | "ERROR";


const WEBSOCK_URL = `${WEBSOCK_BASE_URL}/vet/transcript_ws`;

// App params type with transcriptCallBack
interface InputProps {
  language: string,
  transcriptCallBack?: (transcript: string, is_final: boolean) => void;
}

function Input(props: InputProps) {

  // Store state: mic_state and useRef
  const [micState, setMicState] = React.useState<MicStateString>(
    "OFF",
  );
  const micRef = useRef<MicStateString>(micState);
  const [ws, setWs] = React.useState<WebSocket | null>(null);
  const [mr, setMr] = React.useState<MediaRecorder | null>(null);
  const authContext = useContext(AuthContext);

  const micStateTransition = async () => {
    console.info("mic state transit: " + micState);
    if (micState === "CONNECTING") {
      await startWebsocket();
    } else if (micState === "MICSETUP") {
      await setupMic();
    } else if (micState === "STOPPING") {
      await stopMic();
    } else if (micState === "OFF" || micState === "ERROR") {
      // Close any existing websocket
      if (ws) {
        ws.close();
      }
    }
  }

  const startWebsocket = async () => {
    // Connect to websocket
    if (!authContext || !authContext.token) {
      console.error("No auth token found");
      setMicState("ERROR");
      return;
    }
    const ws = new WebSocket(`${WEBSOCK_URL}?token=${authContext.token}`);
    ws.onopen = () => {
      console.info("Connected to websocket");
      if (micState !== "CONNECTING") {
        ws.close();
        return;
      }
      setWs(ws);
      setMicState("MICSETUP");
    };

    ws.onmessage = (ev) => {
      console.info("Received message: ", ev.data);
      if (props.transcriptCallBack) {
        const data = JSON.parse(ev.data);
        props.transcriptCallBack(data.transcript, data.is_final);
      }
    };

    ws.onerror = (ev) => {
      console.error("Websocket error: ", ev);
      ws.close();
    };

    ws.onclose = (ev: any) => {
      console.info("Websocket closed: ", ev);
      setWs(null);
      const micState = micRef.current;
      if (micState === "STOPPING" || micState === "OFF") {
        setMicState("OFF");
      } else {
        console.error("Websocket closed unexpectedly!, micState: ", micState);
        setMicState("ERROR");
      }
    };
  }


  const setupMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false
      });

      const track = stream.getTracks()[0];
      const sampleRateHz = track.getCapabilities().sampleRate?.max || 48000;

      let langCode;
      if (props.language === "English") {
        langCode = "en-US";
      } else if (props.language === "Tamil") {
        langCode = "ta-IN";
      } else {
        console.error("Invalid language: ", props.language);
        langCode = "en-US";
      }

      try {
        ws?.send(JSON.stringify({
          sampleRateHz,
          langCode,
        }));
      } catch (err) {
        console.error("WS Error sending sample rate: ", err);
        track.stop();
        setMicState("ERROR");
        return;
      }

      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => {
        console.log("Data available: ", ev.data.size, ev.data.type);
        try {
          ws?.send(ev.data);
        } catch (err) {
          console.error("WS Error sending data: ", err);
          setMicState("ERROR");
        }
      };

      mr.onstop = (ev) => {
        track.stop();
        console.log("Media recorder stopped");
        try {
          ws?.send("stop");
        } catch (err) {
          console.error("WS Error sending stop: ", err);
          setMicState("ERROR");
        }
      };

      mr.start(800);
      setInterval(() => {
        mr.stop();
      }, 20000);

      setMr(mr);
      setMicState("ON");

    } catch (err) {

      console.error("Error getting media stream: ", err);
      setMr(null);
      setMicState("ERROR");

    }
  }

  const stopMic = async () => {
    if (mr) {
      mr.stop();
      setMr(null);
    }
  }

  // Use effect to start connection
  useEffect(() => {
    micStateTransition()
      .then(() => { })
      .catch((err) => {
        console.error("Error in micStateTransition: ", err);
        setMicState("ERROR");
      });
  }, [micState]);

  // Render the component

  // Find icon & text for mic_state
  let micIcon;
  let micText;

  if (micState === "OFF") {
    micIcon = faMicrophone;
    micText = "Click";
  } else if (micState === "CONNECTING") {
    micIcon = faEllipsis;
    micText = "Connecting";
  } else if (micState === "ON") {
    micIcon = faMicrophoneAlt;
    micText = "Speak...";
  } else if (micState === "ERROR") {
    micIcon = faMicrophoneSlash;
    micText = "Error!";
  } else if (micState === "MICSETUP") {
    micIcon = faEllipsis;
    micText = "Setup...";
  } else if (micState === "STOPPING") {
    micIcon = faEllipsis;
    micText = "Stopping...";
  } else {
    throw new Error("Invalid state: " + micState);
  }

  // Function to start listening
  const onButtonClick = (ev: any) => {
    console.info("Button clicked, state: ", micState);
    if (micState === "OFF" || micState === "ERROR") {
      setMicState("CONNECTING");
    } else if (micState === "CONNECTING" || micState === "MICSETUP" || micState === "ON") {
      setMicState("STOPPING");
    }
  }

  return (
    <div className="Input">
      <Button onClick={(ev: any) => onButtonClick(ev)}>
        <FontAwesomeIcon icon={micIcon} className="Icon" />
        <p>{micText}</p>
      </Button>
    </div>
  );
}

export default Input;
